/**
 * useUserSettings - Convex-based user settings hook
 *
 * This hook provides real-time synchronized user settings using Convex.
 * It eliminates the race conditions inherent in the old REST API approach
 * by leveraging Convex's real-time subscriptions.
 *
 * Benefits:
 * - Single source of truth: Convex database is the authoritative source
 * - Real-time updates: Settings sync automatically across tabs/devices
 * - Optimistic UI: Local state updates immediately, then syncs with Convex
 * - No race conditions: Convex handles consistency internally
 */

import { useQuery, useMutation } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// Default settings for when user has no settings record yet
const DEFAULT_SETTINGS = {
  theme: 'system' as const,
  emailNotifications: true,
  pushNotifications: true,
  weeklyDigest: false,
  timezone: 'UTC',
  sidebarCollapsed: false,
};

export interface UserSettings {
  theme: 'light' | 'dark' | 'system';
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyDigest: boolean;
  timezone: string;
  sidebarCollapsed: boolean;
  // Extended settings - optional
  unifiedHomeSettings?: unknown;
  filesViewMode?: string;
  privacySettings?: unknown;
  defaultFocusId?: string;
  alwaysStartInFocusMode?: boolean;
  showFocusProgressHeader?: boolean;
  focusLastViewed?: unknown;
  prProfile?: unknown;
  resumeCTADismissed?: boolean;
}

export interface UseUserSettingsReturn {
  /** Current user settings (with defaults applied) */
  settings: UserSettings;
  /** Whether settings are still loading */
  isLoading: boolean;
  /** Whether settings exist in database (vs using defaults) */
  hasSettings: boolean;
  /** Update one or more settings - optimistic update with Convex sync */
  updateSettings: (updates: Partial<UserSettings>) => Promise<void>;
  /** Toggle sidebar collapsed state */
  toggleSidebar: () => Promise<void>;
  /** Reset all settings to defaults */
  resetSettings: () => Promise<void>;
}

/**
 * Hook for accessing and updating user settings via Convex.
 *
 * This replaces the old SettingsContext + useSettingsAPI pattern with
 * a direct Convex subscription, providing real-time sync without race conditions.
 *
 * OPTIMISTIC UPDATES:
 * To provide instant UI feedback, this hook maintains local optimistic state.
 * When you call updateSettings() or toggleSidebar(), the local state updates
 * immediately while the Convex mutation runs in the background. Once the
 * Convex subscription updates, it syncs the local state with the server state.
 *
 * @example
 * ```tsx
 * function Layout() {
 *   const { settings, toggleSidebar, isLoading } = useUserSettings();
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <div className={settings.sidebarCollapsed ? 'collapsed' : 'expanded'}>
 *       <button onClick={toggleSidebar}>Toggle</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUserSettings(): UseUserSettingsReturn {
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Subscribe to settings - this creates a real-time subscription
  // When settings change in the database, this automatically updates
  // Only query when authenticated to avoid "Authentication required" errors
  const settingsData = useQuery(
    api.settings.get,
    isAuthenticated ? {} : "skip"
  );

  // Mutation for updating settings
  const updateSettingsMutation = useMutation(api.settings.update);

  // Mutation for resetting settings
  const resetSettingsMutation = useMutation(api.settings.reset);

  // ============================================================================
  // OPTIMISTIC UPDATE STATE
  // ============================================================================
  // Local state for immediate UI response (optimistic updates)
  // This allows the UI to update instantly while Convex syncs in the background
  const [optimisticUpdates, setOptimisticUpdates] = useState<Partial<UserSettings>>({});

  // Track pending mutations to know when to clear optimistic state
  const pendingMutationsRef = useRef<Set<string>>(new Set());

  // Sync optimistic state with Convex when subscription updates
  // This clears optimistic updates once Convex confirms the change
  useEffect(() => {
    if (settingsData !== undefined) {
      // Clear optimistic updates that have been confirmed by Convex
      setOptimisticUpdates(prev => {
        const newOptimistic: Partial<UserSettings> = {};
        let hasChanges = false;

        // Keep only optimistic updates that differ from server state
        for (const [key, value] of Object.entries(prev)) {
          const serverValue = settingsData?.[key as keyof typeof settingsData];
          if (value !== serverValue) {
            // Use type assertion to handle the generic value type
            (newOptimistic as Record<string, unknown>)[key] = value;
            hasChanges = true;
          }
        }

        // Only update state if there are actual changes to avoid infinite loops
        return hasChanges ? newOptimistic : {};
      });
    }
  }, [settingsData]);

  // Determine loading state
  // settingsData is undefined while loading, null if no settings exist
  // Also loading if auth is still loading
  const isLoading = authLoading || (isAuthenticated && settingsData === undefined);

  // Check if settings exist in database
  const hasSettings = settingsData !== null && settingsData !== undefined;

  // Merge server data with optimistic updates
  // Optimistic updates take precedence for instant UI feedback
  const settings: UserSettings = {
    theme: optimisticUpdates.theme ?? settingsData?.theme ?? DEFAULT_SETTINGS.theme,
    emailNotifications: optimisticUpdates.emailNotifications ?? settingsData?.emailNotifications ?? DEFAULT_SETTINGS.emailNotifications,
    pushNotifications: optimisticUpdates.pushNotifications ?? settingsData?.pushNotifications ?? DEFAULT_SETTINGS.pushNotifications,
    weeklyDigest: optimisticUpdates.weeklyDigest ?? settingsData?.weeklyDigest ?? DEFAULT_SETTINGS.weeklyDigest,
    timezone: optimisticUpdates.timezone ?? settingsData?.timezone ?? DEFAULT_SETTINGS.timezone,
    sidebarCollapsed: optimisticUpdates.sidebarCollapsed ?? settingsData?.sidebarCollapsed ?? DEFAULT_SETTINGS.sidebarCollapsed,
    // Extended settings - pass through as-is (no optimistic updates for these)
    unifiedHomeSettings: settingsData?.unifiedHomeSettings,
    filesViewMode: settingsData?.filesViewMode,
    privacySettings: settingsData?.privacySettings,
    defaultFocusId: settingsData?.defaultFocusId,
    alwaysStartInFocusMode: settingsData?.alwaysStartInFocusMode,
    showFocusProgressHeader: settingsData?.showFocusProgressHeader,
    focusLastViewed: settingsData?.focusLastViewed,
    prProfile: settingsData?.prProfile,
    // resumeCTADismissed is stored inside the generic settings object
    resumeCTADismissed: (settingsData?.settings as { resumeCTADismissed?: boolean } | undefined)?.resumeCTADismissed,
  };

  // Update settings with optimistic UI
  const updateSettings = useCallback(
    async (updates: Partial<UserSettings>) => {
      // Generate a unique ID for this mutation
      const mutationId = `${Date.now()}-${Math.random()}`;
      pendingMutationsRef.current.add(mutationId);

      // Apply optimistic update immediately for instant UI feedback
      setOptimisticUpdates(prev => ({ ...prev, ...updates }));

      try {
        // Transform updates to match Convex mutation args
        // resumeCTADismissed needs to be nested inside the settings object
        const mutationArgs: Record<string, unknown> = { ...updates };

        if ('resumeCTADismissed' in updates) {
          // Move resumeCTADismissed into the settings object
          const { resumeCTADismissed, ...rest } = mutationArgs;
          Object.assign(mutationArgs, rest);
          delete mutationArgs.resumeCTADismissed;
          mutationArgs.settings = { resumeCTADismissed };
        }

        await updateSettingsMutation(mutationArgs);
      } catch (error) {
        // Revert optimistic update on error
        setOptimisticUpdates(prev => {
          const reverted = { ...prev };
          for (const key of Object.keys(updates)) {
            delete reverted[key as keyof UserSettings];
          }
          return reverted;
        });
        console.error('Failed to update settings:', error);
        throw error;
      } finally {
        pendingMutationsRef.current.delete(mutationId);
      }
    },
    [updateSettingsMutation]
  );

  // Convenience function to toggle sidebar with optimistic update
  const toggleSidebar = useCallback(async () => {
    // Use current settings (which includes optimistic state) for toggle
    const newCollapsedState = !settings.sidebarCollapsed;
    await updateSettings({ sidebarCollapsed: newCollapsedState });
  }, [settings.sidebarCollapsed, updateSettings]);

  // Reset settings to defaults
  const resetSettings = useCallback(async () => {
    // Clear all optimistic updates
    setOptimisticUpdates({});

    try {
      await resetSettingsMutation({});
    } catch (error) {
      console.error('Failed to reset settings:', error);
      throw error;
    }
  }, [resetSettingsMutation]);

  return {
    settings,
    isLoading,
    hasSettings,
    updateSettings,
    toggleSidebar,
    resetSettings,
  };
}

export default useUserSettings;
